import React, { useState } from "react";
import { format } from "date-fns";
import { useOutreach, OutreachRequest } from "@/hooks/use-outreach";
import { EnhancedLead } from "@/hooks/use-dashboard-data";
import {
  Send,
  Mail,
  MailOpen,
  MessageSquare,
  CalendarCheck,
  Clock,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Crown, ArrowRight } from "lucide-react";

/* ── Status config ── */
const OUTREACH_STATUS = {
  pending: { label: "Pending", icon: Clock, color: "text-muted-foreground", bg: "bg-muted" },
  sent: { label: "Email Sent", icon: Mail, color: "text-blue-600", bg: "bg-blue-50" },
  opened: { label: "Opened", icon: MailOpen, color: "text-amber-600", bg: "bg-amber-50" },
  replied: { label: "Replied", icon: MessageSquare, color: "text-primary", bg: "bg-primary/10" },
  meeting_scheduled: { label: "Meeting Set", icon: CalendarCheck, color: "text-green-600", bg: "bg-green-50" },
  no_email: { label: "No Email", icon: AlertCircle, color: "text-destructive", bg: "bg-destructive/10" },
} as const;

function OutreachStatusBadge({ status }: { status: string }) {
  const config = OUTREACH_STATUS[status as keyof typeof OUTREACH_STATUS] || OUTREACH_STATUS.pending;
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={cn("gap-1 text-xs", config.color, config.bg, "border-0")}>
      <Icon size={12} />
      {config.label}
    </Badge>
  );
}

/* ── Single outreach item ── */
function OutreachItem({ request }: { request: OutreachRequest }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border rounded-lg p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm truncate">{request.nursing_home_name}</p>
          <p className="text-xs text-muted-foreground">
            {request.email_sent_at
              ? `Sent ${format(new Date(request.email_sent_at), "d MMM yyyy 'at' HH:mm")}`
              : `Created ${format(new Date(request.created_at), "d MMM yyyy")}`}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <OutreachStatusBadge status={request.status} />
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-muted-foreground hover:text-foreground p-1"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-2 text-sm">
          {request.nursing_home_email && (
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">Sent to:</span> {request.nursing_home_email}
            </p>
          )}
          {request.user_message && (
            <div className="bg-muted/40 rounded p-2 text-muted-foreground italic">
              "{request.user_message}"
            </div>
          )}
          {request.notes && (
            <p className="text-muted-foreground">{request.notes}</p>
          )}
          {request.status === 'no_email' && (
            <p className="text-xs text-destructive">
              No email address is available for this nursing home. We're working on alternative contact methods.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Bulk outreach dialog ── */
interface BulkOutreachDialogProps {
  leads: EnhancedLead[];
  existingRequests: OutreachRequest[];
  onSend: (homes: Array<{ id: string; name: string }>, message?: string) => Promise<any>;
}

function BulkOutreachDialog({ leads, existingRequests, onSend }: BulkOutreachDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Filter leads that haven't had outreach yet
  const availableLeads = leads.filter(
    (lead) => !existingRequests.some(
      (r) => r.nursing_home_id === lead.nursing_home_id && r.status !== 'no_email'
    )
  );

  const toggleLead = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    const homes = availableLeads
      .filter((l) => selectedIds.has(l.nursing_home_id))
      .map((l) => ({ id: l.nursing_home_id, name: l.nursing_home_name }));

    if (homes.length === 0) return;

    setSending(true);
    await onSend(homes, message || undefined);
    setSending(false);
    setOpen(false);
    setSelectedIds(new Set());
    setMessage("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" disabled={availableLeads.length === 0}>
          <Send size={16} />
          Reach Out to Nursing Homes
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={18} className="text-primary" />
            Send Outreach Emails
          </DialogTitle>
          <DialogDescription>
            We'll email each nursing home asking about bed availability and CC you on every email so you can reply directly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Home selection */}
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {availableLeads.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                All your saved homes have already been contacted!
              </p>
            ) : (
              availableLeads.map((lead) => (
                <label
                  key={lead.nursing_home_id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedIds.has(lead.nursing_home_id)}
                    onCheckedChange={() => toggleLead(lead.nursing_home_id)}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{lead.nursing_home_name}</p>
                    {lead.location && (
                      <p className="text-xs text-muted-foreground">{lead.location}</p>
                    )}
                  </div>
                </label>
              ))
            )}
          </div>

          {availableLeads.length > 0 && (
            <>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() =>
                    setSelectedIds(new Set(availableLeads.map((l) => l.nursing_home_id)))
                  }
                >
                  Select all
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => setSelectedIds(new Set())}
                >
                  Clear
                </Button>
              </div>

              {/* Optional personal message */}
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Add a personal note (optional)
                </label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g. My mother needs dementia care and we're hoping to find a place within the next 2 months..."
                  rows={3}
                  className="resize-none text-sm"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={selectedIds.size === 0 || sending}
            className="gap-2"
          >
            {sending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={14} />
                Send to {selectedIds.size} home{selectedIds.size !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Premium lock overlay ── */
function PremiumLockOverlay() {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-[2px] rounded-lg">
      <div className="text-center px-6 py-4">
        <Crown className="h-8 w-8 text-amber-500 mx-auto mb-2" />
        <p className="font-semibold text-sm">Premium Feature</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-[220px]">
          Upgrade to have Grandpal reach out to nursing homes on your behalf.
        </p>
        <a
          href="/premium"
          className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary hover:underline"
        >
          Upgrade to Premium <ArrowRight size={12} />
        </a>
      </div>
    </div>
  );
}

/* ── Main panel ── */
interface OutreachPanelProps {
  leads: EnhancedLead[];
  isPremium: boolean;
}

export function OutreachPanel({ leads, isPremium }: OutreachPanelProps) {
  const { requests, isLoading, sendBulkOutreach } = useOutreach();

  const sentCount = requests.filter((r) => r.status === 'sent').length;
  const repliedCount = requests.filter((r) => r.status === 'replied').length;
  const meetingCount = requests.filter((r) => r.status === 'meeting_scheduled').length;

  return (
    <Card className="relative overflow-hidden">
      {!isPremium && <PremiumLockOverlay />}
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Send size={18} className="text-primary" />
              Managed Outreach
              {!isPremium && <Badge variant="outline" className="text-[10px] gap-1 border-amber-300 text-amber-700 bg-amber-50"><Crown size={10} />Premium</Badge>}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              We contact nursing homes on your behalf to check availability
            </p>
          </div>
          {isPremium && (
            <BulkOutreachDialog
              leads={leads}
              existingRequests={requests}
              onSend={sendBulkOutreach}
            />
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats row */}
        {isPremium && requests.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center p-3 rounded-lg bg-blue-50">
              <p className="text-2xl font-bold text-blue-600">{sentCount}</p>
              <p className="text-xs text-blue-600/80">Emails Sent</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-primary/10">
              <p className="text-2xl font-bold text-primary">{repliedCount}</p>
              <p className="text-xs text-primary/80">Replies</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-green-50">
              <p className="text-2xl font-bold text-green-600">{meetingCount}</p>
              <p className="text-xs text-green-600/80">Meetings</p>
            </div>
          </div>
        )}

        {/* Empty state preview for non-premium */}
        {!isPremium && (
          <div className="text-center py-8">
            <Mail className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-medium text-muted-foreground">Automated Outreach</p>
            <p className="text-xs text-muted-foreground mt-1">
              We'll email nursing homes on your shortlist asking about bed availability — you'll be CC'd on every email.
            </p>
          </div>
        )}

        {/* Outreach list for premium users */}
        {isPremium && (
          isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No outreach sent yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Click "Reach Out" to email nursing homes about bed availability. You'll be CC'd on every email.
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {requests.map((req) => (
                <OutreachItem key={req.id} request={req} />
              ))}
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}
